import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronRight, Search } from "lucide-react";
import { useState } from "react";
import { useSettingsGroups } from "~/features/settings/catalog";
import { useT } from "~/lib/i18n";

export const Route = createFileRoute("/_app/settings/")({ component: SettingsHub });

const normalize = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

/**
 * « Réglages » (call of 04/11): one overview over the whole width, every setting with what it is
 * for, a search across titles and descriptions; no long menu beside the pages.
 */
function SettingsHub() {
  const t = useT();
  const groups = useSettingsGroups();
  const [query, setQuery] = useState("");
  const words = normalize(query).split(/\s+/).filter(Boolean);
  const title = (key: string) => t(`settings.pages.${key}.title` as "settings.pages.account.title");
  const summary = (key: string) => t(`settings.hub.summaries.${key}` as "settings.hub.summaries.account");
  const shown = groups
    .map((g) => ({
      ...g,
      pages: g.pages.filter((p) => {
        const text = normalize(`${t(g.label)} ${title(p.key)} ${summary(p.key)}`);
        return words.every((w) => text.includes(w));
      }),
    }))
    .filter((g) => g.pages.length > 0);

  return (
    <div className="flex flex-col gap-6">
      <label className="relative block max-w-xl">
        <Search className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-fg-3" aria-hidden />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("settings.hub.search")}
          aria-label={t("settings.hub.search")}
          className="h-11 w-full rounded-control border border-line-strong bg-surface pr-3 pl-10 text-sm outline-none focus:border-gold"
        />
      </label>

      {shown.length === 0 ? (
        <p className="text-sm text-fg-3">{t("settings.hub.noResult", { query })}</p>
      ) : (
        <div className="columns-1 gap-5 md:columns-2 xl:columns-3">
          {shown.map((group) => (
            <section key={group.label} className="mb-5 break-inside-avoid rounded-panel border border-line bg-surface">
              <h2 className="px-5 pt-4 pb-1 text-[11.5px] font-medium tracking-wide text-fg-3 uppercase">{t(group.label)}</h2>
              <ul className="p-2">
                {group.pages.map((page) => (
                  <li key={page.to}>
                    <Link
                      to={page.to}
                      className="group flex items-center gap-3.5 rounded-control px-3 py-2.5 transition-colors hover:bg-surface-2"
                    >
                      <span className="grid size-9 shrink-0 place-items-center rounded-[10px] bg-sunken text-fg-2 group-hover:text-gold-text">
                        <page.icon className="size-[18px]" aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-fg">{title(page.key)}</span>
                        <span className="line-clamp-2 block text-[13px] leading-snug text-fg-3">{summary(page.key)}</span>
                      </span>
                      <ChevronRight
                        className="size-4 shrink-0 text-fg-3 opacity-0 transition-opacity group-hover:opacity-100"
                        aria-hidden
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
